import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { ClaimerRole } from "../../../../lib/types/claim.types";
import {
  Building2,
  ChevronRight,
  Shield,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { startClaim } from "../../../../lib/api/claim.api";
import { useAuth } from "../../../../Context/AuthContext";

const ROLES: { value: ClaimerRole; label: string; desc: string }[] = [
  {
    value: "Owner",
    label: "Owner",
    desc: "I am the business owner or a registered director",
  },
  {
    value: "Manager",
    label: "Manager",
    desc: "I manage day-to-day operations of this restaurant",
  },
  {
    value: "Authorized staff",
    label: "Authorized Staff",
    desc: "I am authorized by the owner to manage this listing",
  },
  {
    value: "Marketing agency",
    label: "Marketing Agency",
    desc: "I manage online presence on behalf of the business",
  },
];

const TERMS = [
  "I confirm I have the legal right to claim ownership of this listing.",
  "I understand that providing false information may result in account suspension.",
  "I agree to comply with platform terms of service and community guidelines.",
];

export default function ClaimStartPage() {
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const navigate = useNavigate();

  const [selectedRole, setSelectedRole] = useState<ClaimerRole | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { accessToken } = useAuth();

  const canSubmit = selectedRole !== null && termsAccepted && !loading;

  const handleSubmit = async () => {
    if (!canSubmit || !restaurantId || !selectedRole) return;
    setLoading(true);
    setError(null);

    try {
      const result = await startClaim(restaurantId, {
        termsAccepted: true,
        claimerRole: selectedRole,
      }, accessToken);

      if (result.step === "otp_sent") {
        navigate(`/claim/${result.claimId}/verify`);
      } else {
        navigate(`/claim/${result.claimId}/proof`);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F7] font-['DM_Sans',sans-serif]">
      {/* Header bar */}
      <div className="border-b border-[#E8E4DC] bg-white px-6 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <Building2 className="w-5 h-5 text-[#C17B3F]" />
          <span className="text-sm font-medium text-[#6B6460] tracking-wide uppercase">
            Restaurant Ownership Claim
          </span>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px- py-2">
        {/* Title */}
        <div className="mb-10">
          <h1
            className="text-[2.25rem] font-semibold text-[#1C1917] leading-tight mb-3"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Claim this restaurant
          </h1>
          <p className="text-[#6B6460] text-base leading-relaxed">
            To verify your connection to this listing, we'll send a one-time
            code to the restaurant's registered email, or ask you to upload
            supporting documentation.
          </p>
        </div>

        {/* Role selector */}
        <div className="mb-8">
          <label className="block text-sm font-semibold text-[#1C1917] mb-3 tracking-wide uppercase text-xs">
            Your role
          </label>
          <div className="grid gap-3">
            {ROLES.map((role) => {
              const isSelected = selectedRole === role.value;
              return (
                <button
                  key={role.value}
                  onClick={() => setSelectedRole(role.value)}
                  className={`w-full text-left px-5 py-4 rounded-xl border-2 transition-all duration-200 ${
                    isSelected
                      ? "border-[#C17B3F] bg-[#FDF5EC]"
                      : "border-[#E8E4DC] bg-white hover:border-[#C17B3F]/40"
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <span
                        className={`font-semibold text-sm ${
                          isSelected ? "text-[#C17B3F]" : "text-[#1C1917]"
                        }`}
                      >
                        {role.label}
                      </span>
                      <p className="text-xs text-[#6B6460] mt-0.5 leading-relaxed">
                        {role.desc}
                      </p>
                    </div>
                    {isSelected && (
                      <CheckCircle2 className="w-5 h-5 text-[#C17B3F] flex-shrink-0 mt-0.5" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Terms */}
        <div className="mb-8 bg-white border border-[#E8E4DC] rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-4 h-4 text-[#C17B3F]" />
            <span className="text-xs font-semibold uppercase tracking-widest text-[#6B6460]">
              Terms of Verification
            </span>
          </div>
          <ul className="space-y-3 mb-5">
            {TERMS.map((term, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mt-1 w-4 h-4 rounded-full bg-[#F0EAE1] flex items-center justify-center flex-shrink-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C17B3F]" />
                </span>
                <span className="text-sm text-[#44403C] leading-relaxed">
                  {term}
                </span>
              </li>
            ))}
          </ul>
          <label className="flex items-center gap-3 cursor-pointer group">
            <div
              onClick={() => setTermsAccepted(!termsAccepted)}
              className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all duration-150 ${
                termsAccepted
                  ? "bg-[#C17B3F] border-[#C17B3F]"
                  : "border-[#C4B9AD] group-hover:border-[#C17B3F]"
              }`}
            >
              {termsAccepted && (
                <svg
                  className="w-3 h-3 text-white"
                  viewBox="0 0 12 10"
                  fill="none"
                >
                  <path
                    d="M1 5L4.5 8.5L11 1.5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
            <span className="text-sm font-medium text-[#1C1917]">
              I have read and agree to the above terms
            </span>
          </label>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-5 py-4">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className={`w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-semibold text-sm tracking-wide transition-all duration-200 ${
            canSubmit
              ? "bg-[#C17B3F] hover:bg-[#A8652F] text-white shadow-md hover:shadow-lg"
              : "bg-[#E8E4DC] text-[#B0A89E] cursor-not-allowed"
          }`}
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <svg
                className="animate-spin w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="3"
                  className="opacity-25"
                />
                <path
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  className="opacity-75"
                />
              </svg>
              Submitting…
            </span>
          ) : (
            <>
              Continue
              <ChevronRight className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}